import {
	ArrowUpDown,
	ChevronDown,
	ChevronUp,
	Eye,
	EyeOff,
	FlagTriangleRight,
	Inbox,
	TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '@/ui/components/ui/pagination';
import { Spinner } from '@/ui/components/ui/spinner';
import { StatusPlaceholder } from '@/ui/components/ui/status-placeholder';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/ui/components/ui/table';
import type { AdminFeatureRequest } from '../../../hooks/use-admin';
import { FRStatusBadge } from './FRStatusBadge';

type SortKey = 'title' | 'votes' | 'date' | 'status';
type SortDir = 'asc' | 'desc';
type PageItem = number | { ellipsis: true; key: string };

function sortRows(
	rows: AdminFeatureRequest[],
	key: SortKey | null,
	dir: SortDir,
): AdminFeatureRequest[] {
	if (!key) return rows;
	return [...rows].sort((a, b) => {
		let cmp = 0;
		if (key === 'title') cmp = a.title.localeCompare(b.title);
		else if (key === 'votes') cmp = a.upvoteCount - b.upvoteCount;
		else if (key === 'date')
			cmp = Number(a.createdAt) - Number(b.createdAt);
		else if (key === 'status') cmp = a.status.localeCompare(b.status);
		return dir === 'asc' ? cmp : -cmp;
	});
}

function getPageNumbers(current: number, total: number): PageItem[] {
	if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
	const pages: PageItem[] = [1];
	if (current > 3) pages.push({ ellipsis: true, key: 'ellipsis-start' });
	for (
		let i = Math.max(2, current - 1);
		i <= Math.min(total - 1, current + 1);
		i++
	) {
		pages.push(i);
	}
	if (current < total - 2)
		pages.push({ ellipsis: true, key: 'ellipsis-end' });
	pages.push(total);
	return pages;
}

function SortIcon({
	column,
	active,
	dir,
}: {
	column: SortKey;
	active: SortKey | null;
	dir: SortDir;
}) {
	if (active !== column)
		return <ArrowUpDown className='size-3 ml-1.5 opacity-40' />;
	return dir === 'asc' ? (
		<ChevronUp className='size-3 ml-1.5' />
	) : (
		<ChevronDown className='size-3 ml-1.5' />
	);
}

interface FRTableProps {
	rows: AdminFeatureRequest[];
	loading: boolean;
	error: boolean;
	refreshing?: boolean;
}

export function FRTable({
	rows,
	loading,
	error,
	refreshing = false,
}: FRTableProps) {
	const navigate = useNavigate();
	const [sortKey, setSortKey] = useState<SortKey | null>(null);
	const [sortDir, setSortDir] = useState<SortDir>('asc');
	const [page, setPage] = useState(1);

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortKey(key);
			setSortDir('asc');
		}
		setPage(1);
	};

	const sorted = sortRows(rows, sortKey, sortDir);
	const totalPages = Math.max(1, Math.ceil(sorted.length / 10));
	const safePage = Math.min(page, totalPages);
	const pageRows = sorted.slice((safePage - 1) * 10, safePage * 10);

	if (error) {
		return (
			<StatusPlaceholder
				icon={TriangleAlert}
				title='Failed to load feature requests'
				description='Something went wrong. Please try again later.'
				variant='error'
			/>
		);
	}

	if (loading) {
		return (
			<div className='flex items-center justify-center py-24'>
				<Spinner size='lg' />
			</div>
		);
	}

	if (rows.length === 0) {
		return (
			<StatusPlaceholder
				icon={Inbox}
				title='No feature requests'
				description='Feature requests submitted by users will appear here.'
			/>
		);
	}

	return (
		<>
			<div className='relative rounded-2xl shadow-ionos-card bg-card overflow-hidden'>
				{refreshing && (
					<div className='absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[1px]'>
						<Spinner size='lg' />
					</div>
				)}
				<Table>
					<TableHeader className='bg-(--ionos-gray-100)'>
						<TableRow className='hover:bg-(--ionos-gray-200)'>
							<TableHead className='w-8' />
							<TableHead className='w-[50%]'>
								<button
									type='button'
									onClick={() => toggleSort('title')}
									className='flex items-center font-medium text-foreground hover:text-foreground/70 transition-colors'
								>
									Title{' '}
									<SortIcon
										column='title'
										active={sortKey}
										dir={sortDir}
									/>
								</button>
							</TableHead>
							<TableHead className='text-center'>
								<button
									type='button'
									onClick={() => toggleSort('votes')}
									className='flex items-center justify-center w-full font-medium text-foreground hover:text-foreground/70 transition-colors'
								>
									Votes{' '}
									<SortIcon
										column='votes'
										active={sortKey}
										dir={sortDir}
									/>
								</button>
							</TableHead>
							<TableHead>
								<button
									type='button'
									onClick={() => toggleSort('date')}
									className='flex items-center font-medium text-foreground hover:text-foreground/70 transition-colors'
								>
									Date{' '}
									<SortIcon
										column='date'
										active={sortKey}
										dir={sortDir}
									/>
								</button>
							</TableHead>
							<TableHead>
								<button
									type='button'
									onClick={() => toggleSort('status')}
									className='flex items-center font-medium text-foreground hover:text-foreground/70 transition-colors'
								>
									Status{' '}
									<SortIcon
										column='status'
										active={sortKey}
										dir={sortDir}
									/>
								</button>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{pageRows.map((fr) => (
							<TableRow
								key={fr.id}
								className='cursor-pointer'
								onClick={() =>
									navigate(
										`/admin/projects/${fr.projectId}/feature-requests/${fr.id}`,
										{ state: { fr } },
									)
								}
							>
								<TableCell className='text-center px-4'>
									<div className='flex items-center justify-center gap-4'>
										{fr.hide ? (
											<EyeOff className='size-4 text-muted-foreground' />
										) : (
											<Eye className='size-4 text-muted-foreground' />
										)}
										<FlagTriangleRight
											className={
												fr.isFlaggedByModeration
													? 'size-4 text-red-500'
													: 'size-4 text-green-500'
											}
											aria-label={
												fr.isFlaggedByModeration
													? 'Flagged by moderation'
													: 'Not flagged'
											}
										/>
									</div>
								</TableCell>
								<TableCell className='max-w-xs'>
									<p className='font-medium text-sm truncate'>
										{fr.title}
									</p>
									{fr.description && (
										<p className='text-xs text-muted-foreground truncate mt-0.5'>
											{fr.description}
										</p>
									)}
								</TableCell>
								<TableCell className='text-center text-sm font-medium'>
									{fr.upvoteCount}
								</TableCell>
								<TableCell className='text-sm text-muted-foreground'>
									{new Date(
										Number(fr.createdAt),
									).toLocaleDateString('en-GB', {
										day: '2-digit',
										month: 'short',
										year: '2-digit',
									})}
								</TableCell>
								<TableCell>
									<FRStatusBadge status={fr.status} />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{totalPages > 1 && (
				<div className='flex items-center justify-end mt-4'>
					<Pagination className='w-auto mx-0'>
						<PaginationContent>
							<PaginationItem>
								<PaginationPrevious
									href='#'
									onClick={(e) => {
										e.preventDefault();
										setPage((p) => Math.max(1, p - 1));
									}}
									aria-disabled={safePage === 1}
									className={
										safePage === 1
											? 'pointer-events-none opacity-50'
											: ''
									}
								/>
							</PaginationItem>

							{getPageNumbers(safePage, totalPages).map((p) =>
								typeof p !== 'number' ? (
									<PaginationItem key={p.key}>
										<PaginationEllipsis />
									</PaginationItem>
								) : (
									<PaginationItem key={p}>
										<PaginationLink
											href='#'
											isActive={p === safePage}
											onClick={(e) => {
												e.preventDefault();
												setPage(p);
											}}
										>
											{p}
										</PaginationLink>
									</PaginationItem>
								),
							)}

							<PaginationItem>
								<PaginationNext
									href='#'
									onClick={(e) => {
										e.preventDefault();
										setPage((p) =>
											Math.min(totalPages, p + 1),
										);
									}}
									aria-disabled={safePage === totalPages}
									className={
										safePage === totalPages
											? 'pointer-events-none opacity-50'
											: ''
									}
								/>
							</PaginationItem>
						</PaginationContent>
					</Pagination>
				</div>
			)}
		</>
	);
}
